import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../services/api';
import '../Customers/Customers.css';

function SplitOrderModal({ isOpen, onClose, order, onSplitComplete }) {
  const [splitQuantities, setSplitQuantities] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize split quantities when order changes
  useEffect(() => {
    if (order && order.order_lines) {
      const initial = {};
      order.order_lines.forEach(line => {
        initial[line.id] = 0; // Start with 0 - user must explicitly enter quantities to split
      });
      setSplitQuantities(initial);
    }
  }, [order]);

  const handleQuantityChange = (lineId, value) => {
    const qty = parseInt(value) || 0;
    const line = order?.order_lines?.find(l => l.id === lineId);
    const maxQty = line?.qty || 0;
    
    // Ensure quantity doesn't exceed available qty or go negative
    const clampedQty = Math.max(0, Math.min(qty, maxQty));
    setSplitQuantities(prev => ({
      ...prev,
      [lineId]: clampedQty
    }));
  };

  const handleSplitAll = (lineId) => {
    const line = order?.order_lines?.find(l => l.id === lineId);
    if (line) {
      setSplitQuantities(prev => ({
        ...prev,
        [lineId]: line.qty
      }));
    }
  };

  const handleClearSplit = (lineId) => {
    setSplitQuantities(prev => ({
      ...prev,
      [lineId]: 0
    }));
  };

  const hasAnySplit = () => {
    return Object.values(splitQuantities).some(qty => qty > 0);
  };

  const getTotalSplitQuantity = () => {
    return Object.values(splitQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const handleSave = async () => {
    if (!hasAnySplit()) {
      setError('Please enter at least one quantity to split');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Build the splits array - only include lines with qty > 0
      const splits = Object.entries(splitQuantities)
        .filter(([_, qty]) => qty > 0)
        .map(([orderLineId, qty]) => ({
          order_line_id: parseInt(orderLineId),
          qty: qty
        }));

      if (splits.length === 0) {
        setError('Please enter at least one quantity to split');
        setSaving(false);
        return;
      }

      await orderAPI.splitOrder(order.id, { splits });

      onSplitComplete();
      onClose();
    } catch (err) {
      console.error('Error splitting order:', err);
      setError(err.response?.data?.message || 'Failed to split order');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Split Order</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="split-order-info">
            <p><strong>Customer:</strong> {order?.customer?.fullname || 'Unknown Customer'}</p>
            <p><strong>Order Reference:</strong> {order?.order_external_reference || 'N/A'}</p>
            <p><strong>Order Date:</strong> {order?.order_date ? new Date(order.order_date).toLocaleDateString() : 'N/A'}</p>
            {order?.schedule_date && (
              <p><strong>Scheduled:</strong> {new Date(order.schedule_date).toLocaleDateString()}</p>
            )}
          </div>

          {error && (
            <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>
          )}

          <div className="form-section">
            <h3>Select Quantities to Split</h3>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Enter the quantity of each item to move to the new order. The remaining quantity will stay in the original order.
            </p>

            <table className="detail-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Available Qty</th>
                  <th>Split Qty</th>
                  <th>Remaining</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {order?.order_lines?.map((line) => {
                  const splitQty = splitQuantities[line.id] || 0;
                  const remainingQty = line.qty - splitQty;
                  
                  return (
                    <tr key={line.id}>
                      <td>
                        {line.product?.code || line.product_id} 
                        {line.product?.description && ` - ${line.product.description}`}
                      </td>
                      <td style={{ textAlign: 'center' }}>{line.qty}</td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="number"
                          value={splitQty}
                          onChange={(e) => handleQuantityChange(line.id, e.target.value)}
                          min="0"
                          max={line.qty}
                          style={{ width: '70px', textAlign: 'center' }}
                          disabled={saving}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ 
                          color: remainingQty === 0 ? '#999' : remainingQty < 0 ? '#d32f2f' : '#333',
                          fontWeight: remainingQty === 0 ? 'normal' : 'bold'
                        }}>
                          {remainingQty}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleSplitAll(line.id)}
                          disabled={saving || line.qty === 0}
                          style={{ marginRight: '4px' }}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleClearSplit(line.id)}
                          disabled={saving}
                        >
                          None
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Summary:</strong> {getTotalSplitQuantity()} item(s) will be moved to the new order
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !hasAnySplit()}
          >
            {saving ? 'Splitting...' : 'Split Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SplitOrderModal;